import { useLingui } from '@lingui/react/macro';
import type { ReactNode } from 'react';

import { DayLabel } from './day-label';
import { DAY_COLUMNS } from '@/domain/templates/day-of-week';
import type { PreviewColumn } from '@/domain/templates/preview-columns';

import { ShiftPill } from './shift-pill';

interface PreviewDayGridProps {
  /** Per-day total shown under each day label (e.g. heads that day). */
  counts: number[];
  /** Renders the content of the Mon–Sun column at `index`. */
  renderColumn: (index: number) => ReactNode;
}

/**
 * The shared Mon–Sun preview scaffold: a day-label header row (with per-day
 * counts) over seven `preview-day-*` columns whose content is supplied by the
 * caller. Used by {@link PreviewGrid} (per-head pills) and `StackedGrid` (decks).
 */
export function PreviewDayGrid({ counts, renderColumn }: PreviewDayGridProps) {
  return (
    <div className="flex flex-col overflow-hidden rounded-lg border">
      <div className="grid grid-cols-7 border-b bg-muted/30">
        {DAY_COLUMNS.map((column, index) => (
          <div key={column.key} className="border-r p-2.5 text-center last:border-r-0">
            <div className="text-lg font-semibold leading-none text-[var(--heading-foreground)]">
              <DayLabel columnKey={column.key} />
            </div>
            <div className="mt-0.5 text-sm text-foreground">{counts[index] || '–'}</div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {DAY_COLUMNS.map((column, index) => (
          <div
            key={column.key}
            data-testid={`preview-day-${column.key}`}
            className="flex min-w-0 flex-col gap-1 border-r p-2 last:border-r-0"
          >
            {renderColumn(index)}
          </div>
        ))}
      </div>
    </div>
  );
}

interface PreviewGridProps {
  /** Seven Mon–Sun columns, as built by `buildShiftColumns`. */
  columns: PreviewColumn[];
}

/**
 * The per-head preview: one {@link ShiftPill} per required head per day. The
 * un-stacked counterpart to `StackedGrid`. Each column is capped to a sane
 * number of pills; any overflow (a runaway demand-scaled day) collapses into a
 * "+N more" chip so the DOM stays small, while the header shows the true total.
 */
export function PreviewGrid({ columns }: PreviewGridProps) {
  const { t } = useLingui();
  return (
    <PreviewDayGrid
      counts={columns.map((column) => column.total)}
      renderColumn={(index) => {
        const { pills, total } = columns[index];
        const overflow = total - pills.length;
        return (
          <>
            {pills.map((pill) => (
              <ShiftPill
                key={pill.key}
                type={pill.type}
                time={pill.time}
                draft={pill.draft}
                variant={pill.variant}
              />
            ))}
            {overflow > 0 && (
              <div className="rounded-[3px] border border-dashed py-1 text-center text-xs font-medium text-muted-foreground">
                {t`+${overflow} more`}
              </div>
            )}
          </>
        );
      }}
    />
  );
}
